/**
 * STOK HAREKETLERİ — eski sistemde KARŞILIĞI YOKTU.
 *
 * Eski Balder'da stok yalnızca bir SAYIYDI: kim, ne zaman, neden değiştirdi bilinmiyordu;
 * `remove_stock.php` satırı silince geçmiş de siliniyordu. Bu ekran append-only hareket
 * defterini gösterir. Kayıtlar İSTEMCİDEN YAZILAMAZ (Security Rules), yalnızca Cloud
 * Function transaction'ları yazar — her hareket bir idempotency anahtarı taşır.
 *
 * Ekranda maliyet/kâr gösterilmez; yalnızca miktar ve gerekçe.
 */
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { limit as fsLimit, orderBy, where, type QueryConstraint } from "firebase/firestore";

import { DataTable } from "../components/DataTable";
import { Spinner } from "../components/Spinner";
import { EmptyState } from "../components/EmptyState";
import { useCollection } from "../hooks/useCollection";
import { dt, n } from "../lib/format";
import { PageHeader } from "../components";

interface MovementRow {
  id: string;
  /** Yeni model `inventoryKey`; prototipte `kind` + `refId`. İkisi de desteklenir. */
  inventoryKey?: string;
  kind?: string;
  refId?: string;
  itemName?: string;
  itemType?: string;
  delta?: number;
  volumeBefore?: number;
  balanceBefore?: number;
  volumeAfter?: number;
  balanceAfter?: number;
  reason?: string;
  refType?: string;
  sourceType?: string;
  sourceId?: string;
  idempotencyKey?: string;
  insufficient?: boolean;
  forced?: boolean;
  note?: string;
  at?: unknown;
  createdAt?: unknown;
  by?: string;
  userId?: string;
  byName?: string;
}

/** Gerekçe kodları Türkçeye çevrilir — kullanıcı teknik kod görmez. */
const REASON_LABEL: Record<string, string> = {
  "manual-in": "Manuel giriş",
  "manual-out": "Manuel çıkış",
  correction: "Düzeltme",
  stocktake: "Sayım",
  "production-consume": "Üretim tüketimi",
  "stockorder-produce": "Stok siparişi üretimi",
  "stockorder-receive": "Stok siparişi girişi",
  "ssh-consume": "SSH tüketimi",
  rollback: "Geri alma",
};

const SOURCE_LABEL: Record<string, string> = {
  production: "Üretim",
  stockOrder: "Stok siparişi",
  ssh: "SSH",
  manual: "Manuel",
  move: "Transfer",
};

const PAGE_SIZE = 200;

export default function StockMovements() {
  const [params, setParams] = useSearchParams();
  const inventoryId = params.get("inventoryId") ?? "";
  const [reasonFilter, setReasonFilter] = useState("");

  const constraints = useMemo<QueryConstraint[]>(() => {
    const c: QueryConstraint[] = [];
    // Alan adı sürüm farkı: yeni model `inventoryKey`, prototip `refId`.
    if (inventoryId) c.push(where("inventoryKey", "==", inventoryId));
    c.push(orderBy("at", "desc"));
    c.push(fsLimit(PAGE_SIZE));
    return c;
  }, [inventoryId]);

  const { data, loading, error } = useCollection<MovementRow>("inventoryMoves", constraints);

  /**
   * UID → okunabilir ad. `inventoryMoves` yalnızca `by` (UID) taşıyor; `byName`
   * alanı sunucu tarafından KOŞULSUZ boş yazılıyor. Ad çözümü `userProfiles`
   * üzerinden yapılır — bu koleksiyon tam da bunun için var ("kim yaptı"
   * gösterimleri) ve `isActive()` ile okunabilir. Denetim kaydı ekranı
   * (`AuditLog.tsx:118-130`) aynı deseni zaten kullanıyor.
   *
   * Profil bulunamazsa HAM UID gösterilir; sessizce boş bırakılmaz — "kim
   * yaptığı bilinmiyor" ile "kimse yapmadı" aynı şey değildir.
   */
  const profiles = useCollection<{ id: string; fullName?: string | null; name?: string | null; surname?: string | null }>(
    "userProfiles",
  );
  const nameOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of profiles.data) {
      const full = p.fullName ?? [p.name, p.surname].filter(Boolean).join(" ").trim();
      if (full) m.set(p.id, full);
    }
    return (uid: string) => m.get(uid) ?? uid;
  }, [profiles.data]);

  const rows = useMemo(
    () =>
      data
        .filter((r) => (reasonFilter ? r.reason === reasonFilter : true))
        .map((r) => ({
          ...r,
          _key: r.inventoryKey ?? (r.kind && r.refId ? `${r.kind}_${r.refId}` : (r.refId ?? "—")),
          _before: r.volumeBefore ?? r.balanceBefore ?? 0,
          _after: r.volumeAfter ?? r.balanceAfter ?? 0,
          _at: r.at ?? r.createdAt,
          /**
           * ★ `??` BOŞ DİZEYİ GEÇİRMEZ — "Kullanıcı" SÜTUNU HEP BOŞTU.
           *
           * Stok hareketini yazan dört yer de (`stock/consume.ts:377`,
           * `stock/receive.ts:199,340`, `stock/reverse.ts:155`) `byName` alanını
           * KOŞULSUZ `""` olarak yazıyor. Boş dize `null`/`undefined` olmadığı
           * için `??` ona takılıyor ve zincir `by`ye hiç düşmüyordu. Canlı
           * ölçüm: 56 hareketin 36'sında `byName === ""` (kalan 20 onarım
           * betiğinden geliyor), oysa `by` alanı 56/56 dolu.
           *
           * Sonuç: sunucunun ürettiği HER hareket satırında "Kullanıcı" sütunu
           * "—" gösteriyordu; hangi işlemin kim tarafından yapıldığı defterde
           * duruyordu ama ekranda yoktu.
           *
           * `firstFilled` deseni: BOŞ OLMAYAN ilk değer. Aynı tuzağın
           * gerekçesi `lib/productionFields.ts:14-19`'da zaten yazılıydı, bu
           * dosya o düzeltmeyi hiç almamıştı.
           */
          _by: (() => {
            const raw =
              [r.byName, r.by, r.userId].find((v) => typeof v === "string" && v.trim() !== "") ?? "";
            // Sistem yazmaları ("system") ve betik adları olduğu gibi kalır.
            return raw && raw !== "system" ? nameOf(raw) : raw;
          })(),
          /**
           * ★ KAYNAK ALANININ ADI `sourceType`/`sourceId` DEĞİL, `refType`/`refId`.
           * Yazan dört yerin dördü de `refType` + `refId` yazıyor; canlıda
           * `sourceType` ve `sourceId` 56 kaydın SIFIRINDA var. Tür için zaten
           * `refType`e düşülüyordu ama KİMLİK yalnızca `sourceId`den okunuyordu:
           * bu yüzden "Kaynak" sütunundaki üretim bağlantısı hiç oluşmadı.
           */
          _source: r.refType ?? r.sourceType ?? "",
          _sourceId:
            [r.refId, r.sourceId].find((v) => typeof v === "string" && v.trim() !== "") ?? "",
        })),
    [data, reasonFilter, nameOf],
  );

  const reasons = useMemo(() => [...new Set(data.map((r) => r.reason).filter(Boolean))] as string[], [data]);

  return (
    <PageHeader title="Stok Hareketleri">
      <div className="mb-4 flex flex-wrap items-center gap-2 print:hidden">
        <Link
          to="/inventory"
          className="inline-flex min-h-[48px] items-center rounded-md bg-white px-4 py-3 text-base font-semibold text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          ← Stoklar
        </Link>

        <input
          value={inventoryId}
          onChange={(e) =>
            setParams(e.target.value ? { inventoryId: e.target.value } : {})
          }
          placeholder="Stok kalemi (örn. part_120)"
          aria-label="Stok kalemi filtresi"
          className="min-h-[48px] w-full max-w-xs rounded-md border border-gray-300 px-4 text-base shadow-sm sm:w-auto"
        />

        <select
          value={reasonFilter}
          onChange={(e) => setReasonFilter(e.target.value)}
          aria-label="Gerekçe"
          className="min-h-[48px] rounded-md border-0 bg-white px-4 py-3 text-base font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300"
        >
          <option value="">Tüm gerekçeler</option>
          {reasons.map((r) => (
            <option key={r} value={r}>
              {REASON_LABEL[r] ?? r}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => window.print()}
          className="ml-auto inline-flex min-h-[48px] items-center rounded-md bg-white px-4 py-3 text-base font-semibold text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          Yazdır
        </button>
      </div>

      {error ? (
        <div className="mb-4 rounded-lg bg-red-50 p-4 text-base text-red-700">
          Hareket defteri okunamadı: {error}
        </div>
      ) : null}

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Hareket kaydı yok"
          description={
            inventoryId
              ? `${inventoryId} için hareket bulunamadı.`
              : "Stok değiştikçe hareketler burada listelenir."
          }
        />
      ) : (
        <>
          <DataTable
            rowKey={(r) => r._key}
            caption="Stok hareketleri"
            columns={[
              {
                key: "_at",
                header: "Tarih",
                render: (r: (typeof rows)[number]) => dt(r._at as never),
              },
              {
                key: "_key",
                header: "Stok Kalemi",
                render: (r: (typeof rows)[number]) => (
                  <span>
                    <span className="block text-base text-gray-900">{r.itemName ?? r._key}</span>
                    <span className="block text-xs text-gray-500">{r._key}</span>
                  </span>
                ),
              },
              {
                key: "delta",
                header: "Değişim",
                render: (r: (typeof rows)[number]) => (
                  <span
                    className={
                      (r.delta ?? 0) < 0
                        ? "text-base font-bold text-red-600"
                        : "text-base font-bold text-green-700"
                    }
                  >
                    {(r.delta ?? 0) > 0 ? "+" : ""}
                    {n(r.delta)}
                  </span>
                ),
              },
              {
                key: "_before",
                header: "Önce → Sonra",
                render: (r: (typeof rows)[number]) => `${n(r._before)} → ${n(r._after)}`,
              },
              {
                key: "reason",
                header: "Gerekçe",
                render: (r: (typeof rows)[number]) => (
                  <span>
                    <span className="block text-base text-gray-900">
                      {REASON_LABEL[r.reason ?? ""] ?? r.reason ?? "—"}
                    </span>
                    {r.note ? <span className="block text-xs text-gray-500">{r.note}</span> : null}
                    {r.insufficient || r.forced ? (
                      <span className="mt-1 inline-flex items-center rounded-md bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                        Yetersiz stokla tamamlandı
                      </span>
                    ) : null}
                  </span>
                ),
              },
              {
                key: "_source",
                header: "Kaynak",
                render: (r: (typeof rows)[number]) => {
                  const label = SOURCE_LABEL[r._source] ?? r._source ?? "—";
                  if (r._source === "production" && r._sourceId) {
                    return (
                      <Link
                        to={`/production/${encodeURIComponent(r._sourceId)}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {label} · {r._sourceId}
                      </Link>
                    );
                  }
                  return r._sourceId ? `${label} · ${r._sourceId}` : label;
                },
              },
              { key: "_by", header: "Kullanıcı", render: (r) => r._by || "—" },
            ]}
            rows={rows}
          />

          {data.length >= PAGE_SIZE ? (
            <p className="mt-4 text-sm text-gray-500 print:hidden">
              En yeni {PAGE_SIZE} hareket gösteriliyor. Daha eskisi için stok kalemi filtresini
              kullanın.
            </p>
          ) : null}
        </>
      )}
    </PageHeader>
  );
}
